import { useState, useEffect } from "react";
import { Modal, Spin } from "antd";
import { Trans } from "react-i18next";
import classNames from "classnames";
import { useAppDispatch } from "@hooks/redux";
import Image from "@components/Image";
import { formatImgUrl } from "@utils/format";
import CommonButton from "@components/CommonButton";
import TruncateTitle from "@components/TruncateTitle";
import { fetchProductList } from "@pages/library/librarySlice";
import { printProductCategory } from "@utils/printProductCategory";

// 转换数据格式，适配 productItem 的数据结构
export const formatProductData = (product: any) => {
  return {
    id: product.id || product.productId,
    productTitle: product.productTitle || product.product_title,
    productCategory: printProductCategory(product),
    coverImageUrl: product.coverImageUrl || formatImgUrl("product", product.id || product.productId),
    ...product,
  };
};

interface ProductLibraryModalProps {
  open: boolean;
  onClose: () => void;
  /** 提交回调，返回选中的产品数据 */
  onSubmit?: (product: any) => void;
}

const ProductLibraryModal = ({ open, onClose, onSubmit }: ProductLibraryModalProps) => {
  const [libraryList, setLibraryList] = useState<any[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (open) {
      setLibraryLoading(true);
      setSelectedProduct(null);
      dispatch(fetchProductList())
        .then((result: any) => {
          setLibraryList(result.payload || []);
        })
        .catch((error) => {
          console.error("Failed to fetch product library list:", error);
        })
        .finally(() => {
          setLibraryLoading(false);
        });
    }
  }, [open, dispatch]);

  const handleSubmit = () => {
    if (!selectedProduct) return;

    const productData = formatProductData(selectedProduct);
    onSubmit?.(productData);
    onClose();
    setSelectedProduct(null);
  };

  const handleCancel = () => {
    setSelectedProduct(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={982}
      style={{ maxWidth: "none" }}
      title={
        <span className="text-kds-fg font-bold text-[16px]">
          <Trans>Library</Trans>
        </span>
      }
    >
      <Spin spinning={libraryLoading}>
        <div className="overflow-y-auto overflow-x-hidden h-[75vh] p-[4px] -m-[4px] -mr-[20px]">
          {libraryList.length > 0 ? (
            <div className="grid grid-cols-5 gap-[16px] w-[934px]">
              {libraryList.map((item: any) => {
                const productId = item.id || item.productId;
                const isSelected = selectedProduct?.id === productId || selectedProduct?.productId === productId;

                return (
                  <div key={productId} className="relative w-[174px]">
                    <div
                      className="relative cursor-pointer bg-kds-bg-card border border-kds-border rounded-[8px] overflow-hidden transition-all"
                      onClick={() => setSelectedProduct(item)}
                    >
                      <Image
                        url={item.coverImageUrl || formatImgUrl("product", productId)}
                        className="w-full aspect-square object-cover border-none! rounded-none!"
                      />
                      <div className="p-[8px]">
                        <TruncateTitle
                          className="text-[13px] h-[36px]"
                          text={item.product_title || item.productTitle}
                          lineclamp2
                          placement="top"
                        />
                      </div>
                    </div>
                    <div
                      className={classNames(
                        "absolute z-10 -top-[4px] -right-[4px] -bottom-[4px] -left-[4px] border rounded-[8px] hover:border-kds-accent! pointer-events-none",
                        isSelected ? "border-kds-accent!" : "border-transparent",
                      )}
                    ></div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-[400px] text-kds-fg-soft">
              <Trans>Nothing here yet.</Trans>
            </div>
          )}
        </div>
      </Spin>
      <div className="flex justify-end gap-[12px] pt-[16px]">
        <CommonButton size="large" className="min-w-[100px]" onClick={handleCancel}>
          <Trans>Cancel</Trans>
        </CommonButton>
        <CommonButton
          size="large"
          type="primary"
          className="min-w-[100px]"
          disabled={!selectedProduct}
          onClick={handleSubmit}
        >
          <Trans>Submit</Trans>
        </CommonButton>
      </div>
    </Modal>
  );
};

export default ProductLibraryModal;
